/**
 * Grafana Faro frontend observability (Phase 1: RUM + errors).
 *
 * Captures web vitals, uncaught errors/rejections, and route changes, shipping
 * them to a Grafana Cloud Frontend Observability collector. Distributed tracing
 * (frontend→indexer span correlation) is Phase 2 and intentionally not wired
 * here; see the adj-workspace tracking issue.
 *
 * The collector URL is environment-specific (a separate Faro app per env) and
 * is NOT a secret; it embeds a public app key and is safe to commit in
 * .env.development / .env.production. When VITE_GRAFANA_FARO_URL is unset
 * (local dev, or before the Faro app is provisioned) init is a no-op, so the
 * SDK never ships to a dead endpoint.
 */
import {
  createReactRouterV7Options,
  getWebInstrumentations,
  initializeFaro,
  ReactIntegration,
  type Faro,
} from '@grafana/faro-react';
import {
  createRoutesFromChildren,
  matchRoutes,
  Routes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';

let faro: Faro | undefined;

export function initFaro(): Faro | undefined {
  if (faro) return faro;

  const url = import.meta.env.VITE_GRAFANA_FARO_URL;
  if (!url) return undefined;

  faro = initializeFaro({
    url,
    app: {
      name: 'adj.news',
      version: import.meta.env.VITE_APP_VERSION || 'dev',
      environment: import.meta.env.MODE,
    },
    instrumentations: [
      // Console capture is off: logger.ts already routes errors to Faro via
      // pushError (see getFaro), so leaving it on would double-report.
      ...getWebInstrumentations({ captureConsole: false }),
      new ReactIntegration({
        // The app uses component routing (<BrowserRouter> + <Routes>), i.e. the
        // React Router v7 "no data router" setup. Pairs with <FaroRoutes> in
        // App.tsx to emit parameterized route-change events.
        router: createReactRouterV7Options({
          createRoutesFromChildren,
          matchRoutes,
          Routes,
          useLocation,
          useNavigationType,
        }),
      }),
    ],
  });

  return faro;
}

export function getFaro(): Faro | undefined {
  return faro;
}
